import type { CatalogDashboard } from "@shared/types";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Skeleton } from "@shared-ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ImportWorkSections } from "@/features/catalog/components/import-work-sections";
import { getCatalogDashboard } from "@/features/catalog/services/catalog-api.service";

/**
 * The catalogue landing screen: "what needs me today".
 *
 * Plan 008 surface 6 and acceptance V14, and the screen M3 is asked against --
 * "what is stopping these products from publishing?" has to be answerable here,
 * unaided, without anyone running a query.
 *
 * EVERY NUMBER IS A LINK, and that is the design rather than a flourish. A count
 * an operator cannot act on sends them to the products list to rebuild the same
 * filter by hand, which is the work this screen exists to remove. Each tile
 * therefore lands on the list already filtered to the rows it counted.
 *
 * The last two of plan 008's six sections -- the imports waiting on an
 * operator and the sources due for a refresh -- come from the import work
 * queue (issue #255), which costs `admin:catalog:import`; `ImportWorkSections`
 * says so to an operator without it rather than dropping the sections.
 */
export function CatalogDashboardPage() {
	const query = useQuery({
		queryKey: ["admin-catalog-dashboard"],
		queryFn: getCatalogDashboard,
	});

	if (query.isPending) {
		return (
			<div className="grid gap-4">
				<Skeleton className="h-28 rounded-3xl" />
				<Skeleton className="h-56 rounded-3xl" />
				<Skeleton className="h-56 rounded-3xl" />
			</div>
		);
	}

	if (query.isError) {
		/*
		 * An error, not a zeroed dashboard.
		 *
		 * "Nothing is blocked" is the answer an operator most wants and the one
		 * they must not be shown by an outage. Rendering zeros on failure would be
		 * indistinguishable from a healthy, finished catalogue.
		 */
		return (
			<Card className="rounded-3xl border-red-200 bg-red-50/60">
				<CardHeader>
					<CardTitle className="text-red-900">
						Could not load the dashboard
					</CardTitle>
					<CardDescription className="text-red-800">
						These numbers are unknown right now, which is not the same as zero.
						Retry, or work from the{" "}
						<Link className="underline" to="/catalog/products">
							product list
						</Link>{" "}
						until it comes back.
					</CardDescription>
				</CardHeader>
			</Card>
		);
	}

	const data = query.data;

	return (
		<div className="grid gap-4">
			<ProductTotals products={data.products} />
			<PublishBlockers blockers={data.blockers} />
			<div className="grid gap-4 lg:grid-cols-2">
				<StalePrices prices={data.stalePrices} />
				<RecentActivity entries={data.recentActivity} />
			</div>
			<ImportWorkSections />
		</div>
	);
}

/** A number and its label, linking to the list filtered to exactly those rows. */
function Stat({
	label,
	value,
	to,
	tone = "default",
	hint,
}: {
	label: string;
	value: number;
	to?: string;
	tone?: "default" | "warn";
	hint?: string;
}) {
	const body = (
		<>
			<span
				className={
					tone === "warn"
						? "block text-3xl font-semibold tabular-nums text-amber-900"
						: "block text-3xl font-semibold tabular-nums text-slate-900"
				}
			>
				{value}
			</span>
			<span className="mt-1 block text-sm text-slate-600">{label}</span>
			{hint ? (
				<span className="mt-1 block text-xs text-slate-500">{hint}</span>
			) : null}
		</>
	);

	const className =
		tone === "warn"
			? "rounded-2xl border border-amber-200 bg-amber-50/70 p-4"
			: "rounded-2xl border bg-white p-4";

	// A zero is not worth a link: filtering a list to nothing is a dead end, and
	// an operator who clicks one learns only that the screen was not thinking.
	if (!to || value === 0) {
		return <div className={className}>{body}</div>;
	}

	return (
		<Link
			to={to}
			className={`${className} block transition hover:border-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
		>
			{body}
		</Link>
	);
}

function ProductTotals({
	products,
}: {
	products: CatalogDashboard["products"];
}) {
	/*
	 * Verification is rendered from the payload's keys rather than from a
	 * hard-coded list of the four statuses. The enum is a database enum; a fifth
	 * value would appear here the day it is added rather than being silently
	 * dropped by a screen that knows only four.
	 */
	const verification = Object.entries(products.byVerification).sort(
		([a], [b]) => a.localeCompare(b),
	);

	return (
		<Card className="rounded-3xl">
			<CardHeader>
				<CardTitle>The catalogue</CardTitle>
				<CardDescription>
					{products.total} product{products.total === 1 ? "" : "s"} in total.
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-4">
				<div className="grid gap-3 sm:grid-cols-3">
					<Stat
						label="Draft"
						value={products.byStatus.draft}
						to="/catalog/products?status=draft"
					/>
					<Stat
						label="Published"
						value={products.byStatus.active}
						to="/catalog/products?status=active"
					/>
					<Stat
						label="Inactive"
						value={products.byStatus.inactive}
						to="/catalog/products?status=inactive"
					/>
				</div>
				{verification.length > 0 ? (
					<div className="flex flex-wrap items-center gap-2 text-sm">
						<span className="text-slate-600">Verification:</span>
						{verification.map(([status, n]) => (
							<Badge key={status} variant="default" className="capitalize">
								{status.replace(/_/g, " ")}: {n}
							</Badge>
						))}
					</div>
				) : null}
			</CardContent>
		</Card>
	);
}

function PublishBlockers({
	blockers,
}: {
	blockers: CatalogDashboard["blockers"];
}) {
	const ready = blockers.scanned - blockers.blocked;
	/*
	 * BY HOW MUCH the three tiles exceed the blocked count -- not how many
	 * products are double-counted, which is a different number this screen cannot
	 * derive.
	 *
	 * `blocked` counts products; each tile counts products-per-field; so the
	 * difference is the sum over blocked products of (fields - 1). One draft
	 * missing all three things contributes 2, and an earlier version of this
	 * printed that as "2 products are missing more than one thing" above a
	 * blocked count of 1. It was written to replace a sentence that was false in
	 * the ordinary case, and was itself false in a different ordinary case: an
	 * empty draft in a category with no HSN and a required attribute.
	 *
	 * Stating the excess is what the available numbers actually support.
	 * Reporting a product count would need the service to send one.
	 */
	const tileTotal =
		blockers.byField.images +
		blockers.byField.hsnCode +
		blockers.byField.specifications;
	const overlap = tileTotal - blockers.blocked;

	return (
		<Card className="rounded-3xl">
			<CardHeader>
				<CardTitle>What is stopping products from publishing</CardTitle>
				{/*
				 * SCOPED TO THE SCAN when the scan was capped.
				 *
				 * `ready` is `scanned - blocked`, which is right -- readiness is only
				 * knowable for products that were examined. But the old wording said
				 * "900 of 2000 drafts" while the Draft tile above showed 5000, and
				 * the cap notice explaining that sits BELOW this line and says "every
				 * number below is a lower bound". So the one sentence the caveat did
				 * not cover was the one most likely to be read as a claim about the
				 * catalogue.
				 */}
				<CardDescription>
					{blockers.draftTotal === 0
						? "Nothing is waiting: there are no drafts."
						: blockers.capped
							? `${blockers.blocked} of the ${blockers.scanned} drafts scanned cannot be published yet. ${ready} ${
									ready === 1 ? "is" : "are"
								} ready.`
							: `${blockers.blocked} of ${blockers.scanned} draft${
									blockers.scanned === 1 ? "" : "s"
								} cannot be published yet. ${ready} ${
									ready === 1 ? "is" : "are"
								} ready.`}
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-3">
				{blockers.capped ? (
					/*
					 * SAID, not swallowed. "41 need an image" and "41 of the first 2000
					 * need an image" are different sentences, and only one of them is
					 * safe to plan a day around.
					 */
					<p
						role="status"
						className="rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
					>
						Counted the oldest {blockers.scanned} of {blockers.draftTotal}{" "}
						drafts. Every number below is a lower bound, not a total.
					</p>
				) : null}
				<div className="grid gap-3 sm:grid-cols-3">
					<Stat
						label="Need an image"
						value={blockers.byField.images}
						tone="warn"
						to="/catalog/products?status=draft"
					/>
					<Stat
						label="Need an HSN code"
						value={blockers.byField.hsnCode}
						tone="warn"
						to="/catalog/products?status=draft"
						hint="Their category supplies none either"
					/>
					<Stat
						label="Missing a required spec"
						value={blockers.byField.specifications}
						tone="warn"
						to="/catalog/products?status=draft"
					/>
				</div>
				{/*
				 * ONLY WHEN IT IS TRUE. The first version rendered this
				 * unconditionally and asserted the numbers "do not add up" -- which
				 * is false whenever every blocked draft is missing exactly one thing,
				 * the ordinary case on a tidy catalogue, and absurd when there are no
				 * drafts at all: three zeroes under a sentence saying they do not add
				 * up to zero.
				 *
				 * The note exists for the operator who is checking whether the screen
				 * can be trusted. Telling that operator something false, on the one
				 * card whose job is to be believed, is worse than not explaining the
				 * arithmetic at all.
				 */}
				{overlap > 0 ? (
					<p className="text-xs text-slate-500">
						These add up to {tileTotal}, not {blockers.blocked}: a product
						missing more than one thing is counted under each.
					</p>
				) : null}
			</CardContent>
		</Card>
	);
}

function StalePrices({ prices }: { prices: CatalogDashboard["stalePrices"] }) {
	return (
		<Card className="rounded-3xl">
			<CardHeader>
				<CardTitle>Prices past their refresh date</CardTitle>
				<CardDescription>
					{prices.total === 0
						? "No product carries a projected price yet."
						: `${prices.count} of ${prices.total} projected prices have lapsed.`}
				</CardDescription>
			</CardHeader>
			<CardContent>
				{prices.count === 0 ? (
					<p className="text-sm text-slate-600">
						Every projected price is within its category's refresh cadence.
					</p>
				) : (
					<div className="grid gap-2">
						<span className="text-3xl font-semibold tabular-nums text-amber-900">
							{prices.count}
						</span>
						{prices.oldest ? (
							<p className="text-sm text-slate-600">
								The oldest lapsed on{" "}
								<time dateTime={prices.oldest}>
									{new Date(prices.oldest).toLocaleDateString()}
								</time>
								.
							</p>
						) : null}
						{/*
						 * No link, and that is a gap rather than a decision: there is no
						 * screen that lists stale prices. Saying so beats a link that
						 * lands somewhere unhelpful.
						 */}
						<p className="text-xs text-slate-500">
							There is no stale-price list to open yet; the price history for a
							product is on its Pricing tab.
						</p>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

/**
 * Where an activity entry leads, or null when nothing addresses it by id.
 *
 * MERGED BRANDS LINK, and an earlier version suppressed them on a premise that
 * was false twice over. It said `catalog.brand.merged` "retires the loser, and
 * the entry's subject is the id that stopped being a destination". `mergeBrands`
 * does not retire anything -- it sets `mergedIntoId` and deliberately leaves
 * `isActive` alone -- and `getBrandForAdmin` serves superseded brands on
 * purpose, because the brand page is where a merge is explained. Worse, a merge
 * writes TWO audit rows, one on the loser and one on the WINNER, so keying on
 * the action dead-linked a brand that is entirely live.
 *
 * Categories point at the TREE rather than the template editor. The template
 * route costs `admin:catalog:taxonomy`; this screen costs `admin:catalog:read`,
 * and the round-one commit repriced a permission test specifically to prove
 * that. Linking a read-only operator into a permission wall is the same defect
 * as a link that 404s.
 *
 * Variants and price observations do not link: neither has a route addressed by
 * its own id. That is checked against `routes/index.tsx`, not assumed.
 */
function destination(
	entry: CatalogDashboard["recentActivity"][number],
): string | null {
	switch (entry.subjectType) {
		case "catalog_product":
			return `/catalog/products/${entry.subjectId}`;
		case "catalog_brand":
			return `/catalog/brands/${entry.subjectId}`;
		case "catalog_category":
			return "/catalog/categories";
		default:
			return null;
	}
}

/** `catalog.product.updated` -> `product updated`. */
function readableAction(action: string): string {
	return action.replace(/^catalog\./, "").replace(/[._]/g, " ");
}

function RecentActivity({
	entries,
}: {
	entries: CatalogDashboard["recentActivity"];
}) {
	return (
		<Card className="rounded-3xl">
			<CardHeader>
				<CardTitle>Recent catalogue activity</CardTitle>
				<CardDescription>Who changed what, most recent first.</CardDescription>
			</CardHeader>
			<CardContent>
				{entries.length === 0 ? (
					<p className="text-sm text-slate-600">
						No catalogue changes recorded yet.
					</p>
				) : (
					<ul className="grid gap-2 text-sm">
						{entries.map((entry) => (
							<li
								key={entry.id}
								className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b pb-2 last:border-0 last:pb-0"
							>
								<span>
									{/*
									 * Products, brands and categories all link. An earlier
									 * version linked only products, on the stated grounds that
									 * "only products have a route that takes one" -- which was
									 * simply false. Between them brands and categories carry
									 * SEVEN of the fifteen catalogue actions (three brand, four
									 * category, counting both spec-template verbs, which are
									 * filed against the category). Half the feed was dead text
									 * on a screen whose whole design is that every entry is
									 * reachable.
									 *
									 * Variants and price observations still do not link,
									 * because neither has a route addressed by its own id --
									 * that is now a true statement rather than an assumed one.
									 */}
									{destination(entry) ? (
										<Link
											className="font-medium underline"
											to={destination(entry) as string}
										>
											{readableAction(entry.action)}
										</Link>
									) : (
										<span className="font-medium">
											{readableAction(entry.action)}
										</span>
									)}{" "}
									<span className="text-slate-600">by {entry.actor}</span>
								</span>
								<time
									className="text-xs text-slate-500 tabular-nums"
									dateTime={entry.at}
								>
									{new Date(entry.at).toLocaleString()}
								</time>
							</li>
						))}
					</ul>
				)}
			</CardContent>
		</Card>
	);
}
